import { useState, useEffect, useMemo } from 'react';

export const useReturnAddresses = (fgData, approvedReturnItems, customerAddresses, allHaveLabels) => {
  const [selectedCustomerAddress, setSelectedCustomerAddress] = useState(null);
  const [selectedMerchantAddress, setSelectedMerchantAddress] = useState(null);
  const [shouldCalculateRates, setShouldCalculateRates] = useState(false);

  // Get merchant address from fulfillment group with more fallbacks
  const merchantAddress = useMemo(() => {
    if (!fgData) return null;
    
    // More detailed debugging of requests
    console.log('FG Requests Debug:', {
      hasRequests: !!fgData.requests,
      requestCount: fgData.requests?.length || 0,
      requestIds: fgData.requests?.map(r => r.id) || [],
      requestsWithReturnAddress: fgData.requests?.filter(r => !!r.returnToAddress).length || 0
    });
    
    if (fgData.requests?.length > 0) {
      // Log all requests with their returnToAddress
      fgData.requests.forEach((req, i) => {
        console.log(`Request ${i} (ID: ${req.id}):`, {
          hasReturnToAddress: !!req.returnToAddress,
          returnToAddressId: req.returnToAddress?.id,
          returnToAddressDetails: req.returnToAddress ? {
            city: req.returnToAddress.city,
            state: req.returnToAddress.state,
            street: req.returnToAddress.street
          } : null
        });
      });
    }
    
    // First try to get the returnToAddress from any return request in requests array
    if (fgData.requests && fgData.requests.length > 0) {
      for (const request of fgData.requests) {
        if (request.returnToAddress) {
          console.log('SUCCESS: Found returnToAddress in requests:', request.id, request.returnToAddress.id);
          return request.returnToAddress;
        }
      }
    }
    
    // Then check in returnRequests (backward compatibility)
    if (fgData.returnRequests && fgData.returnRequests.length > 0) {
      for (const request of fgData.returnRequests) {
        if (request.returnToAddress) {
          console.log('SUCCESS: Found returnToAddress in returnRequests:', request.id, request.returnToAddress.id);
          return request.returnToAddress;
        }
      }
    }
    
    // Check approved items for returnRequest with returnToAddress
    if (approvedReturnItems && approvedReturnItems.length > 0) {
      for (const item of approvedReturnItems) {
        if (item.returnRequest?.returnToAddress) {
          console.log('SUCCESS: Found returnToAddress in item returnRequest:', item.id, item.returnRequest.returnToAddress.id);
          return item.returnRequest.returnToAddress;
        }
      }
    }
    
    // Debug all possible address locations
    console.log('WARNING: No returnToAddress found, checking fallbacks:', {
      'merchant.shippingAddress': fgData.merchant?.shippingAddress,
      'merchant.addresses[0]': fgData.merchant?.addresses?.[0],
      'orderItems[0].merchant.shippingAddress': fgData.orderItems?.[0]?.merchant?.shippingAddress,
      'orderItems[0].product.merchant.shippingAddress': fgData.orderItems?.[0]?.product?.merchant?.shippingAddress
    });
    
    // Try to get address from different possible locations
    return fgData.merchant?.shippingAddress || 
      fgData.merchant?.addresses?.[0] ||
      fgData.orderItems?.[0]?.merchant?.shippingAddress ||
      fgData.orderItems?.[0]?.product?.merchant?.shippingAddress ||
      null;
  }, [fgData, approvedReturnItems]);

  // Create a returnDetails object from FG data with better merchant address handling
  const returnDetails = useMemo(() => {
    if (!fgData) return null;
    
    // Debug the merchant data to help diagnose the issue
    console.log('Merchant Debug:', {
      merchant: fgData.merchant,
      merchantAddress: merchantAddress,
      orderItems: approvedReturnItems.map(item => ({
        id: item.id,
        merchantAddr: item.merchant?.shippingAddress,
        productMerchantAddr: item.product?.merchant?.shippingAddress
      }))
    });
    
    // Enhance merchant address with merchant details from FG data if available
    const enhancedMerchantAddress = merchantAddress ? {
      ...merchantAddress,
      username: fgData.merchant?.username,
      firstname: fgData.merchant?.firstname,
      lastname: fgData.merchant?.lastname,
      name: fgData.merchant?.username || merchantAddress.name || 'Merchant'
    } : null;
    
    return {
      orderItems: approvedReturnItems,
      returnStatus: fgData.status,
      returnToAddress: enhancedMerchantAddress,
      merchant: fgData.merchant,
      relatedFulfillmentGroup: { id: fgData.id }
    };
  }, [fgData, approvedReturnItems, merchantAddress]);

  // Set default addresses when data is loaded
  useEffect(() => {
    // Don't setup rate calculation if labels already exist
    if (allHaveLabels) {
      console.log('[useReturnAddresses] Labels already exist, skipping address setup for rate calculation');
      return;
    }
    
    // Set default customer address (from addresses)
    if (customerAddresses && customerAddresses.length > 0 && !selectedCustomerAddress) {
      const defaultAddress = customerAddresses.find(addr => addr.isDefault) || customerAddresses[0];
      setSelectedCustomerAddress(defaultAddress);
      
      // If merchant address is also available, enable shipping rate calculation
      if (merchantAddress && !selectedMerchantAddress) {
        console.log('[useReturnAddresses] Both addresses available after loading defaults, will enable rate calculation');
        
        // Small delay to ensure UI updates first
        setTimeout(() => {
          setShouldCalculateRates(true);
        }, 100);
      }
    }
    
    // Set merchant address when available
    if (merchantAddress && !selectedMerchantAddress) {
      // Enhance with merchant details from fgData if available
      const enhancedMerchantAddress = {
        ...merchantAddress,
        // Ensure ID is present
        id: merchantAddress.id || `merchant-${Date.now()}`, // Generate an ID if not present
        // Add merchant contact info if available
        username: merchantAddress.username || fgData?.merchant?.username || 'Merchant',
        firstname: merchantAddress.firstname || fgData?.merchant?.firstname || '',
        lastname: merchantAddress.lastname || fgData?.merchant?.lastname || '',
        name: merchantAddress.name || fgData?.merchant?.username || 'Merchant'
      };
      
      console.log('[useReturnAddresses] Setting merchant address:', enhancedMerchantAddress);
      setSelectedMerchantAddress(enhancedMerchantAddress);
      
      // If customer address is also already set, enable shipping calculation
      if (selectedCustomerAddress) {
        console.log('[useReturnAddresses] Both addresses available after merchant address load, will enable rate calculation');
        
        // Small delay to ensure UI updates first
        setTimeout(() => {
          setShouldCalculateRates(true);
        }, 100);
      }
    } else if (!merchantAddress && fgData?.merchant) {
      // If no specific return address found, create one from merchant data
      console.log('[useReturnAddresses] No return address found, creating from merchant data:', fgData.merchant);
      
      // Create a basic address from merchant info
      const fallbackMerchantAddress = {
        id: `merchant-${Date.now()}`,
        name: fgData.merchant.username || 'Merchant',
        username: fgData.merchant.username,
        firstname: fgData.merchant.firstname,
        lastname: fgData.merchant.lastname,
        // Use a default US address if none available
        street: fgData.merchant.address?.street || '123 Merchant St',
        city: fgData.merchant.address?.city || 'New York',
        state: fgData.merchant.address?.state || 'NY',
        postalCode: fgData.merchant.address?.postalCode || '10001',
        country: fgData.merchant.address?.country || 'US',
      };
      
      console.log('[useReturnAddresses] Created fallback merchant address:', fallbackMerchantAddress);
      setSelectedMerchantAddress(fallbackMerchantAddress);
      
      // If customer address is also already set, enable shipping calculation
      if (selectedCustomerAddress) {
        console.log('[useReturnAddresses] Both addresses available after fallback merchant address, will enable rate calculation');
        
        // Small delay to ensure UI updates first
        setTimeout(() => {
          setShouldCalculateRates(true);
        }, 100);
      }
    }
  }, [customerAddresses, merchantAddress, selectedCustomerAddress, selectedMerchantAddress, fgData, allHaveLabels]);

  // When addresses are ready, allow rate calculation - but don't auto-trigger it
  useEffect(() => {
    // Don't calculate rates if all items already have labels
    if (allHaveLabels) {
      console.log('[useReturnAddresses] All items already have labels, skipping rate calculation');
      setShouldCalculateRates(false);
      return;
    }
    
    if (selectedCustomerAddress && selectedMerchantAddress) {
      console.log('[useReturnAddresses] Both addresses loaded, enabling shipping rate calculation', {
        customerAddress: {
          id: selectedCustomerAddress.id,
          city: selectedCustomerAddress.city,
          state: selectedCustomerAddress.state
        },
        merchantAddress: {
          id: selectedMerchantAddress.id,
          city: selectedMerchantAddress.city,
          state: selectedMerchantAddress.state
        }
      });
      // Auto-trigger rate calculation when both addresses are ready
      setShouldCalculateRates(true);
    } else {
      console.log('[useReturnAddresses] Waiting for addresses:', { 
        hasCustomerAddress: !!selectedCustomerAddress, 
        hasMerchantAddress: !!selectedMerchantAddress,
        merchantAddressData: selectedMerchantAddress
      });
      setShouldCalculateRates(false);
    }
  }, [selectedCustomerAddress, selectedMerchantAddress, allHaveLabels]);

  return {
    selectedCustomerAddress,
    setSelectedCustomerAddress,
    selectedMerchantAddress,
    setSelectedMerchantAddress,
    shouldCalculateRates,
    setShouldCalculateRates,
    returnDetails,
    merchantAddress
  };
}; 